import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { VideoInfoModalService } from '@core/components/video-info-modal/video-info-modal.service';
import { Equipment, Recurrence } from '@core/interfaces';
import { LocationEnvironment } from '@core/interfaces/locations-environments';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DialogLinkComponent } from './dialog-link/dialog-link.component';
import { DialogLinkData, GetLinkedItemsResult } from './dialog-link/dialog-link.types';
import { TabLinksService } from './tab-links.service';
import { ReloadItemSubject, UnlinkItemSubject } from './tab-links.types';

@Component({
  selector: 'tab-links',
  templateUrl: './tab-links.component.html',
  styleUrls: ['./tab-links.component.scss']
})
export class TabLinksComponent implements OnInit, OnDestroy {
  @Input() public form: FormGroup;
  @Input() public isViewMode: boolean;
  @Input() public recurrence: Recurrence;

  public searchControl = new FormControl();
  public hasLinkedItems = false;
  public locationLinks: TreeViewItem[] = [];
  public unlinkedEquipments: TreeViewItem[] = [];
  public destroy$ = new Subject();

  constructor(
    private readonly matDialog: MatDialog,
    private readonly tabLinksService: TabLinksService,
    private readonly translateService: TranslateService,
    private readonly videoInfoModalService: VideoInfoModalService,
  ) {}

  public populateLinkedItems(): void {
    const equipments = this.recurrence.equipments;
    const sectors = this.recurrence.locationSectors;
    const environments = this.recurrence.locationEnvironments;

    this.hasLinkedItems = Boolean(
      environments.length ||
      equipments.length ||
      sectors.length
    );

    if (!this.hasLinkedItems) {
      return;
    }

    const { sectors: sectorsLinks, unlinkedEquipments } = this.tabLinksService.linkSectorsEnvironmentsEquipments({
      sectors,
      environments,
      equipments
    });


    const locationLinksUnfiltered = this.tabLinksService.formatSectors({
      sectors: sectorsLinks,
      environments,
      equipments,
      isViewMode: this.isViewMode,
      recurrence: this.recurrence
    });

    const unlinkedEquipmentsUnfiltered = this.tabLinksService.formatUnlinkedEquipments({
      unlinkedEquipments,
      isViewMode: this.isViewMode,
      recurrence: this.recurrence
    });

    this.locationLinks = this.filter(locationLinksUnfiltered, this.searchControl.value);
    this.unlinkedEquipments = this.filter(unlinkedEquipmentsUnfiltered, this.searchControl.value);
  }

  public ngOnInit(): void {

    this.tabLinksService.unlinkItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: UnlinkItemSubject) => {
        this.clearFormLinks(result);
        this.populateLinks();
      });

    this.tabLinksService.reloadItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ReloadItemSubject) => {
        this.updateFormLinks(value);
        this.populateLinks();
      });

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(500))
      .subscribe(() => {
        this.populateLinks();
      });

    merge(this.form.get('segment').valueChanges, this.form.get('location').valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.populateLinks();
      });

    this.searchControl.setValue('');
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openDialogLink(): void {
    const segment = this.form.get('segment').value;
    const location = this.form.get('location').value;

    if (!segment || !location) {
      return;
    }

    this.matDialog.open(DialogLinkComponent, {
      panelClass: 'field-dialog-860',
      autoFocus: false,
      data: {
        segmentId: segment.id,
        locationId: location.id,
        linkedEquipments: this.form.get('newEquipments').value,
        linkedSectors: this.form.get('locationSectors').value,
        linkedEnvironments: this.form.get('locationEnvironments').value,
      } as DialogLinkData
    }).afterClosed().subscribe((result?: GetLinkedItemsResult) => {
      if (result) {
        this.form.get('newEquipments').setValue(result.equipments);
        this.form.get('locationSectors').setValue(result.sectors);
        this.form.get('locationEnvironments').setValue(result.environments);
      }

      this.searchControl.setValue('');
      this.populateLinks();
    });
  }

  private updateFormLinks(params: ReloadItemSubject): void {
    const formValue = this.form.get(params.formControlName);
    const index = formValue.value.findIndex((item) => item.id === params.updatedValue.id);
    formValue.value[index] = params.updatedValue;
    this.form.get(params.formControlName).setValue(formValue.value);

    if (params.updatedValue.archived) {
      formValue.value.splice(index, 1);
      this.clearFormLinks({
        id: params.updatedValue.id,
        formControlName: params.formControlName
      });
    }

    if (params.formControlName = 'newEquipments') {
      const hasEnvironmentOnForm = params.updatedValue.locationEnvironment && this.form.get('locationEnvironments').value
        .some((environment) => environment.id === params.updatedValue.locationEnvironment.id);

      if (params.updatedValue.locationEnvironment && !hasEnvironmentOnForm) {
        formValue.value.splice(index, 1);
        this.clearFormLinks({
          id: params.updatedValue.id,
          formControlName: params.formControlName
        });
      }

      const hasSectorOnForm = params.updatedValue.locationSector && this.form.get('locationSectors').value
        .some((sector) => sector.id === params.updatedValue.locationSector.id);

      if (params.updatedValue.locationSector && !hasSectorOnForm) {
        formValue.value.splice(index, 1);
        this.clearFormLinks({
          id: params.updatedValue.id,
          formControlName: params.formControlName
        });
      }
    }

    this.populateLinks();
  }

  private clearFormLinks(params: UnlinkItemSubject): void {
    const maintenanceFormValue = this.form.getRawValue();
    const newValue = maintenanceFormValue[params.formControlName].filter((item) => item.id !== params.id);
    this.form.get(params.formControlName).setValue(newValue);

    if (params.formControlName === 'locationSectors') {
      const environments = maintenanceFormValue.locationEnvironments.filter((environment: LocationEnvironment) => (
        environment.locationSector.id !== params.id
      ));

      const equipments = maintenanceFormValue.newEquipments.filter((equipment: Equipment) => (
        !equipment.locationSector || equipment.locationSector.id !== params.id
      ));

      this.form.get('newEquipments').setValue(equipments);
      this.form.get('locationEnvironments').setValue(environments);
    }

    if (params.formControlName === 'locationEnvironments') {
      const equipments = maintenanceFormValue.newEquipments.filter((equipment: Equipment) => (
        !equipment.locationEnvironment || equipment.locationEnvironment.id !== params.id
      ));

      this.form.get('newEquipments').setValue(equipments);
    }
  }

  private populateLinks(): void {

    const equipments = this.form.get('newEquipments').value || [];
    const sectors = this.form.get('locationSectors').value || [];
    const environments = this.form.get('locationEnvironments').value || [];

    this.hasLinkedItems = Boolean(
      (equipments && equipments.length) ||
      (sectors && sectors.length) ||
      (environments && environments.length)
    );


    const searchValue = this.searchControl.value && this.searchControl.value.trim().toLowerCase();

    const { sectors: sectorsLinks, unlinkedEquipments } = this.tabLinksService.linkSectorsEnvironmentsEquipments({
      sectors,
      environments,
      equipments
    });

    const locationLinksUnfiltered = this.tabLinksService.formatSectors({
      sectors: sectorsLinks,
      environments,
      isViewMode: this.isViewMode,
      equipments
    });

    const unlinkedEquipmentsUnfiltered = this.tabLinksService.formatUnlinkedEquipments({
      unlinkedEquipments,
      isViewMode: this.isViewMode,
    });

    this.locationLinks = this.tabLinksService.filter(locationLinksUnfiltered, searchValue);
    this.unlinkedEquipments = this.tabLinksService.filter(unlinkedEquipmentsUnfiltered, searchValue);
  }

  public openDemonstrationVideoDialog(): void {
    this.videoInfoModalService.open({
      title: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.TITLE'),
      description: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.DESCRIPTION'),
      videoId: 'VQE_FyV8Gf0'
    });
  }

  private filter(items: TreeViewItem[], searchTerm: string): TreeViewItem[] {
    searchTerm = (searchTerm || '').toLowerCase().trim();

    return items.filter((item) => {
      if (item.children) {
        item.children = this.filter(item.children, searchTerm);
      }

      const text = (item.text || '').toLowerCase();
      const description = (item.description || '').toLowerCase();

      return text.includes(searchTerm) || description.includes(searchTerm) || (item.children && item.children.length) || this.parentMatch(item, searchTerm);
    });
  }

  private parentMatch(item: TreeViewItem, searchTerm: string): boolean {
    if (!item.parent) { return false; }

    searchTerm = (searchTerm || '').toLowerCase().trim();

    const text = (item.parent.text || '').toLowerCase();
    const description = (item.parent.description || '').toLowerCase();

    return text.includes(searchTerm) || description.includes(searchTerm) || this.parentMatch(item.parent, searchTerm);
  }
}
